/**
 * The visitor's CMP answer, mirrored into a first-party cookie on `.extern.com` so that BOTH
 * `www.extern.com` and `app.extern.com` can read the one decision.
 *
 * 🔴 WHY THIS EXISTS. Both Cookiebot tags carry `data-cookiedomain=".extern.com"`, but the cookie
 * it writes is scoped to the host that wrote it. Measured on production, 2026-09-20: accept on
 * `www.extern.com/post/...` and `CookieConsent` is present there and reports granted; open
 * `app.extern.com` in the same browser and the cookie is absent and Cookiebot reports
 * `hasResponse: false`, while every other `.extern.com` cookie (our own visitor id, `_ga`) does
 * cross. So a visitor who answers on the marketing site arrives at the app as if they had never
 * been asked, and the app's vendors — Mixpanel among them — stay off.
 *
 * The visible symptom was the Labs preview: it runs in a frame inside the guide pages, its reader
 * has already answered on the article, and from Sep 18 not one of its events reached Mixpanel.
 * The same gap applies to every www → app journey, which is part of the company-wide drop in
 * tracked sign-ups after the consent release.
 *
 * 🔴 WHAT THIS IS NOT. It does not decide anything, soften anything, or infer consent. It carries
 * a decision the visitor made, in Cookiebot, on this same registrable domain, to the other host of
 * the same site. The rules it follows:
 *
 * - Written ONLY from a real CMP answer (`hasResponse === true`). Never from a default, never from
 *   a guess, never from a URL.
 * - A decline is mirrored exactly like an accept. This is not a grant channel: `{statistics:false}`
 *   travels as faithfully as `{statistics:true}`, and a visitor who says no on the article is
 *   denied in the app without being asked again.
 * - Read ONLY when the CMP in THIS window has no answer of its own. Cookiebot always wins where it
 *   has spoken, so withdrawing or changing consent in either place takes effect here immediately.
 * - It expires with the same 12 months Cookiebot uses for its own record, and a mirror older than
 *   that is ignored.
 * - `SameSite=Lax`, not readable cross-site, no personal data: four booleans, a timestamp and the
 *   CMP configuration id, which is already public in the page source.
 *
 * Deleting this cookie changes nothing except that the app asks again, which is the behaviour we
 * have today.
 */

export interface MirroredConsent {
  /** When the answer was mirrored, ISO. Used only to expire a stale record. */
  at: string;
  categories: {
    marketing: boolean;
    necessary: boolean;
    preferences: boolean;
    statistics: boolean;
  };
  /** The Cookiebot domain-group id the answer was given under. */
  cmp: string;
}

export const CONSENT_MIRROR_COOKIE = '__extern_consent';

/** Matches Cookiebot's own retention. A mirror older than this is treated as no answer at all. */
const MAX_AGE_DAYS = 365;
const MAX_AGE_SECONDS = MAX_AGE_DAYS * 24 * 60 * 60;

/**
 * The registrable domain, derived from the host rather than hard-coded, so a preview deployment
 * (`*.vercel.app`) writes a cookie for itself and localhost writes a host-only one. Returning
 * undefined means "host-only", which is the safe default: the mirror simply does not travel.
 */
function cookieDomain(hostname: string): string | undefined {
  return hostname === 'extern.com' || hostname.endsWith('.extern.com') ? '.extern.com' : undefined;
}

export function readConsentMirror(now: Date = new Date()): MirroredConsent | undefined {
  if (typeof document === 'undefined') {
    return;
  }

  const raw = document.cookie
    .split('; ')
    .find((entry) => entry.startsWith(`${CONSENT_MIRROR_COOKIE}=`))
    ?.slice(CONSENT_MIRROR_COOKIE.length + 1);

  if (!raw) {
    return;
  }

  try {
    const parsed = JSON.parse(decodeURIComponent(raw)) as Partial<MirroredConsent>;
    const { categories, cmp, at } = parsed;

    // Anything malformed is treated as no answer rather than as a denial, so a broken cookie can
    // never be mistaken for a decision the visitor made.
    if (!(categories && typeof cmp === 'string' && typeof at === 'string')) {
      return;
    }
    if (typeof categories.statistics !== 'boolean' || typeof categories.marketing !== 'boolean') {
      return;
    }

    const age = now.getTime() - new Date(at).getTime();
    if (!Number.isFinite(age) || age < 0 || age > MAX_AGE_SECONDS * 1000) {
      return;
    }

    return {
      at,
      categories: {
        marketing: categories.marketing,
        necessary: true,
        preferences: categories.preferences === true,
        statistics: categories.statistics,
      },
      cmp,
    };
  } catch {
    return;
  }
}

export function writeConsentMirror(answer: Omit<MirroredConsent, 'at'>, now: Date = new Date()): void {
  if (typeof document === 'undefined') {
    return;
  }

  const value = encodeURIComponent(JSON.stringify({ ...answer, at: now.toISOString() }));
  const domain = cookieDomain(window.location.hostname);
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';

  const attributes = `Path=/; Max-Age=${MAX_AGE_SECONDS}; SameSite=Lax${domain ? `; Domain=${domain}` : ''}${secure}`;

  // biome-ignore lint/suspicious/noDocumentCookie: the Cookie Store API is not in Safari or Firefox, and this has to work everywhere the banner does
  document.cookie = `${CONSENT_MIRROR_COOKIE}=${value}; ${attributes}`;
}
